import { clsx } from "clsx";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
	MenuIcon,
	Minimize2Icon,
	PlusIcon,
	SparklesIcon,
	XIcon,
} from "lucide-react";
import { useCallback, useState } from "react";

import ChatContentSubtitle from "./ChatContentSubtitle";
import ChatConversationPane from "./ChatConversationPane";
import ChatHistory from "./ChatHistory";
import ChatSignedOut from "./ChatSignedOut";
import Button from "~/components/Button";
import type { ChatController } from "~/hooks/useChatController";
import useGlobalStore from "~/hooks/useGlobalStore";

interface ChatImmersiveProps {
	controller: ChatController;
	// Render inline (e.g. as a dashboard page) instead of as a fullscreen
	// overlay — drops the backdrop and the collapse/close chrome.
	embedded?: boolean;
}

const ChatImmersive = ({
	controller,
	embedded = false,
}: ChatImmersiveProps) => {
	const reduceMotion = useReducedMotion();
	const { collapseChat } = useGlobalStore();
	const { isSignedIn, thread, handleSelectThread, handleNewChat, handleClose } =
		controller;

	const [isHistoryOpen, setIsHistoryOpen] = useState(false);

	const handleSelectThreadFromSidebar = useCallback(
		(threadId: number) => {
			handleSelectThread(threadId);
			setIsHistoryOpen(false);
		},
		[handleSelectThread],
	);

	const handleNewChatFromSidebar = useCallback(() => {
		handleNewChat();
		setIsHistoryOpen(false);
	}, [handleNewChat]);

	const sidebarBody = (
		<>
			<div className="flex shrink-0 items-center gap-2.5 px-4 py-3.5">
				<span className="flex size-6 items-center justify-center rounded-md bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-sm">
					<SparklesIcon className="size-3.5" />
				</span>
				<span className="font-semibold text-slate-900 text-sm dark:text-white">
					AI Tutor
				</span>
			</div>

			{isSignedIn && (
				<div className="px-3 pb-2">
					<Button
						type="button"
						onClick={handleNewChatFromSidebar}
						variant="outlined"
						size="sm"
						className="w-full gap-1.5"
					>
						<PlusIcon className="size-4" />
						New chat
					</Button>
				</div>
			)}

			{isSignedIn && (
				<ChatHistory onSelectThread={handleSelectThreadFromSidebar} />
			)}
		</>
	);

	return (
		<>
			{!embedded && (
				<motion.div
					key="immersive-backdrop"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					onClick={collapseChat}
					className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
				/>
			)}

			<motion.div
				key="immersive-panel"
				initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
				animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
				exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
				transition={{ duration: 0.2, ease: "easeOut" }}
				className={clsx(
					"flex bg-white dark:bg-slate-900",
					embedded ? "relative h-full w-full" : "fixed inset-0 z-50",
				)}
			>
				<aside className="tablet:flex hidden min-h-0 w-72 shrink-0 flex-col border-slate-200 border-r dark:border-slate-800">
					{sidebarBody}
				</aside>

				<AnimatePresence>
					{isHistoryOpen && (
						<>
							<motion.div
								key="history-backdrop"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								onClick={() => setIsHistoryOpen(false)}
								className="fixed inset-0 z-40 tablet:hidden bg-slate-900/40 backdrop-blur-sm"
							/>
							<motion.aside
								key="history-drawer"
								initial={reduceMotion ? { opacity: 0 } : { x: "-100%" }}
								animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
								exit={reduceMotion ? { opacity: 0 } : { x: "-100%" }}
								transition={
									reduceMotion
										? { duration: 0.2 }
										: { type: "spring", stiffness: 320, damping: 34 }
								}
								className="fixed inset-y-0 left-0 z-50 flex tablet:hidden min-h-0 w-[85%] max-w-xs flex-col border-slate-200 border-r bg-white dark:border-slate-800 dark:bg-slate-900"
							>
								{sidebarBody}
							</motion.aside>
						</>
					)}
				</AnimatePresence>

				<div className="flex min-h-0 min-w-0 flex-1 flex-col">
					<div className="flex shrink-0 items-center justify-between border-slate-100 border-b px-5 py-3 dark:border-slate-800">
						<div className="flex min-w-0 items-center gap-2">
							<button
								type="button"
								onClick={() => setIsHistoryOpen(true)}
								aria-label="Open chat history"
								className="tablet:hidden rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
							>
								<MenuIcon className="size-5" />
							</button>
							<div className="flex min-w-0 flex-col">
								<span className="line-clamp-1 font-semibold text-slate-900 text-sm dark:text-white">
									{thread?.title ?? "AI Tutor"}
								</span>
								<ChatContentSubtitle controller={controller} />
							</div>
						</div>
						{!embedded && (
							<div className="flex items-center gap-1">
								<motion.button
									type="button"
									onClick={collapseChat}
									aria-label="Collapse"
									whileHover={reduceMotion ? undefined : { scale: 1.12 }}
									whileTap={reduceMotion ? undefined : { scale: 0.9 }}
									className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
								>
									<Minimize2Icon className="size-4" />
								</motion.button>
								<motion.button
									type="button"
									onClick={handleClose}
									aria-label="Close"
									whileHover={reduceMotion ? undefined : { scale: 1.12 }}
									whileTap={reduceMotion ? undefined : { scale: 0.9 }}
									className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
								>
									<XIcon className="size-5" />
								</motion.button>
							</div>
						)}
					</div>

					<div className="mx-auto flex w-full max-w-3xl flex-1 flex-col overflow-hidden">
						{!isSignedIn && <ChatSignedOut />}
						{isSignedIn && <ChatConversationPane controller={controller} />}
					</div>
				</div>
			</motion.div>
		</>
	);
};

export default ChatImmersive;
